// Editor-only composition assist: which piano keys belong to the chosen key/scale.
// Purely visual — it never restricts note entry, playback or the stored sequence.

export const ASSIST_ROOT_NAMES = Object.freeze(['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'])

export const ASSIST_SCALES = Object.freeze({
  major: Object.freeze({ label: 'Major', intervals: Object.freeze([0, 2, 4, 5, 7, 9, 11]) }),
  minor: Object.freeze({ label: 'Minor', intervals: Object.freeze([0, 2, 3, 5, 7, 8, 10]) }),
})

export const DEFAULT_COMPOSITION_ASSIST = Object.freeze({ enabled: false, root: 0, scale: 'major' })

export function midiPitchClass(midi) {
  const value = Math.trunc(Number(midi))
  if (!Number.isFinite(value)) return null
  return ((value % 12) + 12) % 12
}

export function isScaleRoot(midi, rootPitchClass) {
  const pitchClass = midiPitchClass(midi)
  return pitchClass != null && pitchClass === midiPitchClass(rootPitchClass)
}

export function isNoteInScale(midi, rootPitchClass, scale) {
  const intervals = ASSIST_SCALES[scale]?.intervals
  const pitchClass = midiPitchClass(midi)
  const root = midiPitchClass(rootPitchClass)
  if (!intervals || pitchClass == null || root == null) return false
  return intervals.includes(((pitchClass - root) + 12) % 12)
}

// 'root' | 'in-scale' | 'normal' — 'normal' whenever assist is off or unusable.
export function assistKeyState(midi, assist) {
  if (!assist?.enabled) return 'normal'
  if (isScaleRoot(midi, assist.root)) return 'root'
  return isNoteInScale(midi, assist.root, assist.scale) ? 'in-scale' : 'normal'
}

// One place decides which piano key state wins: sounding keys always beat assist hints.
export function resolvePianoKeyState({ isActive = false, isSelected = false, assistState = 'normal' } = {}) {
  if (isActive) return 'active'
  if (isSelected) return 'selected'
  return assistState === 'root' || assistState === 'in-scale' ? assistState : 'normal'
}

// Geometry stays in the template; only hue/weight is computed here so the assist strip can
// follow the active track colour without a second colour table.
const ASSIST_STRIP_WEIGHT = {
  root: { opacity: 1, whiteFilter: 'brightness(0.85)', blackFilter: 'brightness(1.25)' },
  'in-scale': { opacity: 0.7, whiteFilter: 'brightness(0.85)', blackFilter: 'brightness(1.25)' },
}

export function assistStripStyle({ color, indicator, isWhiteKey = true } = {}) {
  const weight = ASSIST_STRIP_WEIGHT[indicator]
  if (!weight || !color) return null
  return {
    backgroundColor: color,
    opacity: weight.opacity,
    filter: isWhiteKey ? weight.whiteFilter : weight.blackFilter,
  }
}

export function compositionAssistLabel(rootPitchClass, scale) {
  const root = midiPitchClass(rootPitchClass)
  const label = ASSIST_SCALES[scale]?.label
  return root == null || !label ? '' : `${ASSIST_ROOT_NAMES[root]} ${label}`
}

export function compositionAssistValue(rootPitchClass, scale) {
  return `${midiPitchClass(rootPitchClass) ?? 0}:${scale}`
}

export function parseCompositionAssistValue(value) {
  const [rawRoot, scale] = String(value ?? '').split(':')
  const root = midiPitchClass(rawRoot)
  if (root == null || !ASSIST_SCALES[scale]) return null
  return { root, scale }
}

export function compositionAssistOptions() {
  return ASSIST_ROOT_NAMES.flatMap((_, root) => Object.keys(ASSIST_SCALES).map(scale => ({
    value: compositionAssistValue(root, scale),
    label: compositionAssistLabel(root, scale),
    root,
    scale,
  })))
}
